import { useMemo, useState } from "react";
import type { DailyRecordRow } from "@/lib/database.types";

function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

interface Props {
  records: DailyRecordRow[];
  selected: string;            // YYYY-MM-DD
  onSelect: (date: string) => void;
}

/**
 * Month calendar with a dot on every day that has a check-in. Clicking a
 * past (or today's) day selects it for viewing/backfilling in the check-in
 * card — future days are disabled. Today gets the brand-green ring.
 */
export function RecordsCalendar({ records, selected, onSelect }: Props) {
  const todayISO = localISO(new Date());
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const logged = useMemo(() => new Set(records.map((r) => r.record_date)), [records]);

  const weeks = useMemo(() => {
    const year = monthStart.getFullYear();
    const month = monthStart.getMonth();
    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay()); // back up to the Sunday on/before the 1st
    const out: Array<Array<{ iso: string; day: number; inMonth: boolean }>> = [];
    const cur = new Date(start);
    do {
      const week: Array<{ iso: string; day: number; inMonth: boolean }> = [];
      for (let i = 0; i < 7; i++) {
        week.push({ iso: localISO(cur), day: cur.getDate(), inMonth: cur.getMonth() === month });
        cur.setDate(cur.getDate() + 1);
      }
      out.push(week);
    } while (cur.getMonth() === month);
    return out;
  }, [monthStart]);

  const monthLabel = monthStart.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const navBtn =
    "w-7 h-7 grid place-items-center rounded-[8px] border border-fh-border text-fh-gray hover:text-fh-ink hover:bg-fh-cream transition";

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold text-fh-ink">{monthLabel}</div>
        <div className="flex gap-1.5">
          <button
            type="button" aria-label="Previous month" className={navBtn}
            onClick={() => setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
          >
            ‹
          </button>
          <button
            type="button" aria-label="Next month" className={navBtn}
            onClick={() => setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
          >
            ›
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {DOW.map((d) => (
          <div key={d} className="text-[10.5px] font-semibold uppercase tracking-wider text-fh-gray py-1">
            {d}
          </div>
        ))}
        {weeks.flat().map(({ iso, day, inMonth }) => {
          const isToday = iso === todayISO;
          const isSelected = iso === selected;
          const isFuture = iso > todayISO;
          const hasRecord = logged.has(iso);
          return (
            <button
              key={iso}
              type="button"
              disabled={isFuture}
              onClick={() => onSelect(iso)}
              aria-pressed={isSelected}
              aria-label={`${iso}${hasRecord ? " (logged)" : ""}`}
              className={[
                "relative h-9 rounded-[9px] text-[12.5px] font-semibold transition flex flex-col items-center justify-center",
                !inMonth ? "text-fh-gray-light" : isFuture ? "text-fh-gray-light" : "text-fh-ink",
                isSelected
                  ? "bg-fh-green text-white"
                  : isToday
                    ? "ring-1 ring-inset ring-fh-green bg-fh-sage/60"
                    : !isFuture
                      ? "hover:bg-fh-cream"
                      : "",
                isFuture ? "cursor-not-allowed" : "cursor-pointer",
              ].join(" ")}
            >
              <span className="leading-none">{day}</span>
              <span
                aria-hidden="true"
                className={[
                  "w-1 h-1 rounded-full mt-[3px]",
                  hasRecord ? (isSelected ? "bg-white" : "bg-fh-green") : "bg-transparent",
                ].join(" ")}
              />
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-fh-gray-light mt-2 m-0">
        Tap any past day to view or add its check-in.
      </p>
    </div>
  );
}
